import type {
  ActionItemRow,
  ApprovalEvent,
  AssessmentResultView,
  AssessmentWizardView,
  BoundaryItem,
  ClassificationClass,
  ClassificationScore,
  CriterionRow,
  EvidenceSummary,
  GlobalFilters,
  IncludedApplicationRow,
  IncludedProcessRow,
  QueryResult,
  WizardStep,
} from '@aurora-pvi/contracts';
import { CLASSIFICATION_CLASS_LABELS, ROUTES, buildPath } from '@aurora-pvi/contracts';
import type { Assessment } from '@aurora-pvi/seed';
import {
  ASSESSMENT_CRITERIA,
  ASSESSMENT_MODEL_VERSION,
  round,
  weightedScoreOutOf100,
} from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, canApprove, canEdit, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata, chartPayload } from '../metadata';
import { applicationRef, ownerRef, simpleRef, valueStreamRef } from '../refs';
import type { CandidateListRow, PagedRows } from '../types';

/**
 * Product discovery: the candidate list, the assessment wizard and its result.
 *
 * `isApproved` on the decision summary is hard-coded false. The engine
 * recommends; a person decides. Making that a constant rather than a computed
 * field means no future change to the scoring can accidentally auto-approve a
 * classification.
 */

const ALL_STEPS: WizardStep[] = ['Overview', 'Attributes', 'Evidence', 'Classification', 'Review'];

/** How a criterion pattern maps onto each operating construct. */
const CLASS_AFFINITY: Record<string, Partial<Record<ClassificationClass, number>>> = {
  Q01: { BusinessProduct: 1, PlatformProduct: 0.4, SharedService: 0.5, DataProduct: 0.6 },
  Q02: { BusinessProduct: 1, PlatformProduct: 0.5, SharedService: 0.4 },
  Q03: {
    BusinessProduct: 0.9,
    PlatformProduct: 0.8,
    SharedService: 0.7,
    ApplicationComponent: 0.4,
  },
  Q04: { BusinessProduct: 1, PlatformProduct: 0.9, ProjectInitiative: 0.1, SharedService: 0.6 },
  Q05: { BusinessProduct: 0.9, PlatformProduct: 0.8, SharedService: 0.8 },
  Q06: { BusinessProduct: 1, DataProduct: 0.8, SharedService: 0.5 },
  Q07: { BusinessProduct: 0.9, PlatformProduct: 0.9, ProjectInitiative: 0.1 },
  Q08: { BusinessProduct: 1, PlatformProduct: 0.7, ApplicationComponent: 0.1 },
  Q09: { BusinessProduct: 1, PlatformProduct: 0.6, SharedService: 0.4 },
  Q10: { BusinessProduct: 0.9, PlatformProduct: 0.8, UtilityCommodity: 0.2 },
};

const CANDIDATE_CLASSES: ClassificationClass[] = [
  'BusinessProduct',
  'PlatformProduct',
  'DataProduct',
  'SharedService',
  'ApplicationComponent',
  'UtilityCommodity',
  'ProjectInitiative',
];

function alternativesFor(assessment: Assessment): ClassificationScore[] {
  const scores = CANDIDATE_CLASSES.map((classification) => {
    let weighted = 0;
    let weightTotal = 0;
    for (const criterion of ASSESSMENT_CRITERIA) {
      const score = assessment.scores.find((s) => s.criterionId === criterion.criterionId)?.score;
      if (score === null || score === undefined) continue;
      const affinity = CLASS_AFFINITY[criterion.criterionId]?.[classification] ?? 0.3;
      weighted += (score / 5) * criterion.weightPercent * affinity;
      weightTotal += criterion.weightPercent;
    }
    const fit = weightTotal === 0 ? 0 : round(weighted / weightTotal, 4);
    return {
      classification,
      label: CLASSIFICATION_CLASS_LABELS[classification],
      fit,
      weightedScore: round(weighted, 2),
      band: (fit >= 0.7 ? 'High' : fit >= 0.45 ? 'Moderate' : 'Low') as ClassificationScore['band'],
    };
  });
  return scores.sort((a, b) => b.fit - a.fit);
}

export function createDiscoverRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  const criterionRows = (assessment: Assessment): CriterionRow[] =>
    ASSESSMENT_CRITERIA.map((criterion) => {
      const scored = assessment.scores.find((s) => s.criterionId === criterion.criterionId);
      return {
        criterionId: criterion.criterionId,
        ordinal: criterion.ordinal,
        name: criterion.name,
        guidingQuestion: criterion.guidingQuestion,
        weightPercent: criterion.weightPercent,
        iconToken: criterion.iconToken,
        score: scored?.score ?? null,
        rationale: scored?.rationale ?? null,
        evidenceIds: (scored?.evidenceCodes ?? [])
          .map((code) => dataset.evidence.find((e) => e.businessCode === code)?.id)
          .filter((id): id is string => id !== undefined),
        isMandatory: criterion.isMandatory,
        scaleAnchors: criterion.scaleAnchors,
      };
    });

  const averageScore = (assessment: Assessment): number | null => {
    const scored = assessment.scores
      .map((s) => s.score)
      .filter((score): score is 1 | 2 | 3 | 4 | 5 => score !== null);
    if (scored.length === 0) return null;
    return round(scored.reduce((sum, s) => sum + s, 0) / scored.length, 2);
  };

  return {
    async listCandidates(
      filters: GlobalFilters,
    ): Promise<QueryResult<PagedRows<CandidateListRow>>> {
      const scope = applyFilters(dataset, index, user, filters);

      const rows: CandidateListRow[] = dataset.assessments
        .filter((assessment) => {
          if (!assessment.productCode)
            return scope.isEnterpriseScope || scope.scopeType === 'Department';
          return scope.scopeProductCodes.has(assessment.productCode);
        })
        .map((assessment) => {
          const alternatives = alternativesFor(assessment);
          const best = alternatives[0];
          const scored = assessment.scores.some((s) => s.score !== null);
          return {
            id: assessment.id,
            businessCode: assessment.candidateCode,
            name: assessment.candidateName,
            domain: assessment.domain,
            valueStream: valueStreamRef(dataset, assessment.valueStreamCode),
            assessmentId: assessment.id,
            assessmentCode: assessment.businessCode,
            status: assessment.status,
            scoreOutOf100: scored ? weightedScoreOutOf100(assessment.scores) : null,
            recommended: assessment.recommended,
            recommendedLabel: CLASSIFICATION_CLASS_LABELS[assessment.recommended],
            confidence:
              best && best.fit >= 0.7 ? 'High' : best && best.fit >= 0.45 ? 'Medium' : 'Low',
            owner: ownerRef(index, user, assessment.ownerPersonCode),
            lastUpdatedAt: assessment.lastSavedAt,
            openWarnings: assessment.warnings.length,
            route:
              assessment.status === 'Approved'
                ? buildPath(ROUTES.discoverResults, { assessmentId: assessment.id })
                : buildPath(ROUTES.discoverWizard, { candidateId: assessment.id }),
          };
        });

      return resolved({
        data: { rows, totalRows: rows.length, hasMore: false },
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.discoverCandidates,
          sources: PAGE_SOURCES.discover,
          confidence: 'Medium',
          actions: [
            { action: 'createCandidate', permission: 'create', enabled: canEdit(user) },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },

    async getWizard(candidateId: string): Promise<QueryResult<AssessmentWizardView>> {
      const scope = applyFilters(dataset, index, user, {});
      const assessment =
        dataset.assessments.find((a) => a.id === candidateId) ??
        dataset.assessments.find((a) => a.candidateCode === candidateId) ??
        dataset.assessments.find((a) => a.businessCode === candidateId);
      if (!assessment) throw new Error('Assessment not found');

      const criteria = criterionRows(assessment);
      const unanswered = criteria.filter((c) => c.score === null);
      const mandatoryUnanswered = unanswered.filter((c) => c.isMandatory);
      const alternatives = alternativesFor(assessment);
      const best = alternatives[0];
      const isReadOnly = assessment.status === 'Approved' || !canEdit(user);

      const finalizeBlockers: string[] = [];
      if (mandatoryUnanswered.length > 0) {
        finalizeBlockers.push(
          `${mandatoryUnanswered.length} mandatory criteria are not yet scored (${mandatoryUnanswered
            .map((c) => c.criterionId)
            .join(', ')}).`,
        );
      }
      if (assessment.warnings.some((w) => w.severity === 'Error')) {
        finalizeBlockers.push('An anti-pattern error must be resolved before finalising.');
      }
      if (criteria.every((c) => c.evidenceIds.length === 0)) {
        finalizeBlockers.push('At least one criterion must carry evidence.');
      }

      const view: AssessmentWizardView = {
        candidate: {
          id: assessment.id,
          businessCode: assessment.candidateCode,
          name: assessment.candidateName,
          domain: assessment.domain,
          processContext: assessment.processContext,
          valueStream: valueStreamRef(dataset, assessment.valueStreamCode),
          l2Domain: assessment.l2DomainCode,
          assessmentOwner: ownerRef(index, user, assessment.ownerPersonCode),
          lastUpdated: assessment.lastSavedAt,
        },
        assessmentId: assessment.id,
        currentStep: assessment.currentStep,
        completedSteps: assessment.completedSteps,
        blockedSteps:
          mandatoryUnanswered.length > 0 ? ALL_STEPS.filter((step) => step === 'Review') : [],
        criteria,
        averageScore: averageScore(assessment),
        decision:
          best && unanswered.length < ASSESSMENT_CRITERIA.length
            ? {
                recommended: best.classification,
                recommendedLabel: best.label,
                explanation: `Scored ${weightedScoreOutOf100(assessment.scores)} of 100 across ten weighted criteria. The strongest signals are outcome orientation and persistent funding; the weakest is technology coupling.`,
                confidencePercent: round(best.fit * 100, 1),
                confidenceBand: best.fit >= 0.7 ? 'High' : best.fit >= 0.45 ? 'Medium' : 'Low',
                // Always false. A recommendation is not a decision.
                isApproved: false,
                modelVersion: ASSESSMENT_MODEL_VERSION,
                warnings: assessment.warnings,
              }
            : null,
        scoreBreakdown: criteria.map((criterion) => ({
          criterionId: criterion.criterionId,
          name: criterion.name,
          score: criterion.score,
          maxScore: 5 as const,
        })),
        alternatives,
        saveState: 'saved',
        lastSavedAt: assessment.lastSavedAt,
        isReadOnly,
        canFinalize: finalizeBlockers.length === 0 && !isReadOnly,
        finalizeBlockers,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: buildPath(ROUTES.discoverWizard, { candidateId: assessment.id }),
          sources: PAGE_SOURCES.discover,
          confidence: best && best.fit >= 0.7 ? 'High' : 'Low',
          actions: [
            { action: 'saveDraft', permission: 'edit', enabled: !isReadOnly },
            {
              action: 'finalize',
              permission: 'propose',
              enabled: finalizeBlockers.length === 0 && !isReadOnly,
              disabledReason: finalizeBlockers[0],
            },
            {
              action: 'approve',
              permission: 'approve',
              enabled: false,
              disabledReason: 'Approval happens at the portfolio board, not in the wizard.',
            },
          ],
        }),
      });
    },

    async getResult(assessmentId: string): Promise<QueryResult<AssessmentResultView>> {
      const scope = applyFilters(dataset, index, user, {});
      const assessment =
        dataset.assessments.find((a) => a.id === assessmentId) ??
        dataset.assessments.find((a) => a.businessCode === assessmentId);
      if (!assessment) throw new Error('Assessment not found');

      const alternatives = alternativesFor(assessment);
      const best = alternatives[0];
      const total = weightedScoreOutOf100(assessment.scores);
      const productCode = assessment.productCode;

      const includedApplications: IncludedApplicationRow[] = productCode
        ? dataset.productApplicationLinks
            .filter((link) => link.productCode === productCode)
            .map((link): IncludedApplicationRow | null => {
              const application = index.applicationsByCode.get(link.applicationCode);
              if (!application) return null;
              return {
                application: applicationRef(application),
                businessCapability: application.businessCapability,
                criticality: application.criticality,
                roleInProduct: link.role,
              };
            })
            .filter((row): row is IncludedApplicationRow => row !== null)
        : [];

      const includedProcesses: IncludedProcessRow[] = productCode
        ? dataset.productProcessLinks
            .filter((link) => link.productCode === productCode)
            .map((link) => {
              const process = dataset.processes.find((p) => p.businessCode === link.processCode);
              if (!process) return null;
              const parent = dataset.processes.find((p) => p.businessCode === process.parentCode);
              return {
                processL2: parent?.name ?? '—',
                processL3: process.name,
                processRef: simpleRef(process.id, 'Process', process.businessCode, process.name),
                roleInProduct: link.role,
              };
            })
            .filter((row): row is IncludedProcessRow => row !== null)
        : [];

      const product = productCode ? index.productsByCode.get(productCode) : undefined;
      const boundaryItems = (inScope: boolean): BoundaryItem[] =>
        (product?.boundary ?? [])
          .filter((entry) => entry.inScope === inScope)
          .map((entry) => ({
            id: entry.id,
            label: entry.label,
            entityRef: null,
            inScope: entry.inScope,
            reason: entry.reason,
            evidenceId: entry.evidenceCode
              ? (dataset.evidence.find((e) => e.businessCode === entry.evidenceCode)?.id ?? null)
              : null,
          }));

      const evidenceCodes = new Set(assessment.scores.flatMap((s) => s.evidenceCodes));
      const evidence: EvidenceSummary[] = dataset.evidence
        .filter(
          (record) =>
            evidenceCodes.has(record.businessCode) || record.businessCode.startsWith('EVD-CDR'),
        )
        .map((record) => ({
          id: record.id,
          title: record.title,
          sourceLabel: record.sourceLabel,
          date: record.date,
          status: record.status,
          // Restricted evidence is listed but not linkable — its existence is
          // not the secret; its contents are.
          url: record.status === 'Available' ? record.url : null,
          owner: ownerRef(index, user, record.ownerPersonCode),
        }));

      const decision = dataset.decisions.find((d) => d.subjectCode === assessment.businessCode);
      const approvals: ApprovalEvent[] = decision
        ? dataset.approvals
            .filter((step) => step.decisionCode === decision.businessCode)
            .map((step) => ({
              stage: step.stage,
              actor: ownerRef(index, user, step.actorPersonCode),
              action: step.action,
              occurredAt: step.occurredAt,
              comments: step.comments,
              effectiveDate: step.effectiveDate,
            }))
        : [];

      const nextActions: ActionItemRow[] = [
        {
          id: `${assessment.id}-a1`,
          action: 'Publish the agreed boundary to the process-to-product mapping set.',
          owner: ownerRef(index, user, 'PPL-0008'),
          ownerOrganization: 'Portfolio Office',
          targetDate: dataset.periodEnd,
          status: 'InProgress',
        },
        {
          id: `${assessment.id}-a2`,
          action: 'Confirm allocation basis for shared crew applications with Finance.',
          owner: ownerRef(index, user, 'PPL-0058'),
          ownerOrganization: 'Finance Business Partnering',
          targetDate: null,
          status: 'Planned',
        },
        {
          id: `${assessment.id}-a3`,
          action: 'Review the platform-overlap warning at the next architecture forum.',
          owner: ownerRef(index, user, 'PPL-0005'),
          ownerOrganization: 'Enterprise Architecture',
          targetDate: null,
          status: 'Blocked',
        },
      ];

      const view: AssessmentResultView = {
        assessment: {
          id: assessment.id,
          businessCode: assessment.businessCode,
          candidateName: assessment.candidateName,
          completedAt: assessment.completedAt,
          completedBy: ownerRef(index, user, assessment.completedByPersonCode),
          status: assessment.status,
          modelVersion: assessment.modelVersion,
        },
        recommendation: {
          classification: best?.classification ?? assessment.recommended,
          label: CLASSIFICATION_CLASS_LABELS[best?.classification ?? assessment.recommended],
          fitLabel: best ? `${best.band} fit` : 'Insufficient evidence',
          scoreOutOf100: total,
          rationaleSummary:
            decision?.rationale ??
            'Scored against ten weighted criteria; see the weighted score table for the contribution of each.',
        },
        radar: chartPayload({
          chartId: `assessment-radar-${assessment.businessCode}`,
          title: 'Criterion profile',
          accessibleSummary: `Scores from 1 to 5 across the ten assessment criteria for ${assessment.candidateName}.`,
          xLabel: 'Criterion',
          series: [
            {
              seriesId: 'score',
              label: 'Score',
              points: ASSESSMENT_CRITERIA.map((criterion) => ({
                x: criterion.name,
                y:
                  assessment.scores.find((s) => s.criterionId === criterion.criterionId)?.score ??
                  null,
              })),
            },
          ],
        }),
        weightedScores: ASSESSMENT_CRITERIA.map((criterion) => {
          const score =
            assessment.scores.find((s) => s.criterionId === criterion.criterionId)?.score ?? 0;
          return {
            criterionId: criterion.criterionId,
            attribute: criterion.name,
            weightPercent: criterion.weightPercent,
            score,
            weightedScore: round((score / 5) * criterion.weightPercent, 2),
            iconToken: criterion.iconToken,
          };
        }),
        weightedTotal: { raw: round(total / 20, 2), outOf100: total },
        keyReasons: [
          {
            text: 'Owns a named operational outcome with measurable customer impact.',
            sentiment: 'positive',
          },
          {
            text: 'Persistent team and multi-year funding already in place.',
            sentiment: 'positive',
          },
          { text: 'Boundary survives replacing any single crew system.', sentiment: 'positive' },
          {
            text: 'Three in-scope capabilities are consumed by other products — monitor for platform drift.',
            sentiment: 'caution',
          },
          {
            text: 'Cost allocation for shared crew applications is not yet agreed.',
            sentiment: 'caution',
          },
        ],
        evidence,
        boundary: { inScope: boundaryItems(true), outOfScope: boundaryItems(false) },
        includedApplications: {
          rows: includedApplications,
          totalRows: includedApplications.length,
          hasMore: false,
        },
        includedProcesses: {
          rows: includedProcesses,
          totalRows: includedProcesses.length,
          hasMore: false,
        },
        excludedItems: (product?.boundary ?? [])
          .filter((entry) => !entry.inScope)
          .map((entry) => ({ label: entry.label, reason: entry.reason ?? 'Out of scope.' })),
        ownerRecommendation: {
          recommendedOwnerTitle: 'Product Owner, Crew Disruption Recovery',
          ownerOrganization: 'Network & Operations — Crew Operations',
          rationale:
            'The accountable owner should sit with the value stream that carries the disruption cost, not with the systems that support it.',
        },
        gapsAndAssumptions: [
          {
            text: 'Allocation of shared crew application cost is assumed at the current usage split.',
            severity: 'Warning',
            kind: 'assumption',
          },
          {
            text: 'Crew agreement extract is restricted; duty-limit rules taken on trust from People & Workplace.',
            severity: 'Warning',
            kind: 'gap',
          },
          {
            text: 'Day-of-ops walkthrough notes are superseded and need refreshing.',
            severity: 'Info',
            kind: 'gap',
          },
          {
            text: 'Benefit baseline assumes FY2026 disruption volumes.',
            severity: 'Info',
            kind: 'assumption',
          },
        ],
        nextActions,
        approvals,
        alternatives,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: buildPath(ROUTES.discoverResults, { assessmentId: assessment.id }),
          sources: PAGE_SOURCES.discover,
          confidence: best && best.fit >= 0.7 ? 'High' : 'Medium',
          actions: [
            { action: 'export', permission: 'export', enabled: true },
            {
              action: 'approve',
              permission: 'approve',
              enabled: canApprove(user) && assessment.status !== 'Approved',
              disabledReason:
                assessment.status === 'Approved' ? 'Already approved and published.' : undefined,
            },
          ],
        }),
      });
    },
  };
}
